import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { CalendarClock, Plus, Trash2 } from "lucide-react";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { CheckboxField, NumberField, SelectField, TextField } from "@/components/admin/fields";
import { Badge } from "@/components/ui/badge";
import type { BadgeProps } from "@/components/ui/badge";
import { toast } from "@/components/feedback/ToastHost";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { errorMessage } from "@/lib/errors";
import {
  createBay,
  createBaySchedule,
  deleteBay,
  deleteBaySchedule,
  getBay,
  listBaySchedules,
  listBays,
  updateBay,
} from "@/services/api/bays";
import { listSites } from "@/services/api/sites";
import type { Bay } from "@/services/api/types";

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

interface BayForm {
  site_id: string;
  name: string;
  slot_duration_minutes: string;
  is_active: boolean;
  loads_from_rear: boolean;
  loads_from_left: boolean;
  loads_from_right: boolean;
  max_vehicle_length_mm: string;
}

const emptyForm: BayForm = {
  site_id: "",
  name: "",
  slot_duration_minutes: "60",
  is_active: true,
  loads_from_rear: true,
  loads_from_left: false,
  loads_from_right: false,
  max_vehicle_length_mm: "",
};

/** Bay administration: full CRUD plus per-bay operating-window (time slot) editing. */
export function BaysPage() {
  const { t } = useTranslation();
  const [siteFilter, setSiteFilter] = useState("");
  const [schedulesFor, setSchedulesFor] = useState<Bay | null>(null);

  const queryClient = useQueryClient();
  const sites = useQuery({ queryKey: ["sites", "all"], queryFn: () => listSites({ limit: 500 }) });
  const bays = useQuery({
    queryKey: ["bays", siteFilter],
    queryFn: () => listBays(siteFilter ? Number(siteFilter) : undefined),
  });
  const schedules = useQuery({
    queryKey: ["bay-schedules", schedulesFor?.id],
    queryFn: () => (schedulesFor ? listBaySchedules(schedulesFor.id) : Promise.resolve([])),
    enabled: schedulesFor !== null,
  });

  const addSchedule = useMutation({
    mutationFn: (data: { weekday: number; opens_at: string; closes_at: string }) =>
      createBaySchedule({ bay_id: schedulesFor!.id, ...data }),
    onSuccess: () => {
      toast.success(t("admin.saved"));
      void queryClient.invalidateQueries({ queryKey: ["bay-schedules"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const removeSchedule = useMutation({
    mutationFn: (id: number) => deleteBaySchedule(id),
    onSuccess: () => {
      toast.success(t("admin.deleted"));
      void queryClient.invalidateQueries({ queryKey: ["bay-schedules"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const siteName = (siteId: number | null) =>
    sites.data?.items.find((s) => s.id === siteId)?.name ?? "—";

  const columns: ColumnDef<Bay>[] = [
    { accessorKey: "name", header: t("admin.bay") },
    { accessorKey: "site_id", header: t("admin.site"), cell: ({ getValue }) => siteName(getValue() as number) },
    { accessorKey: "slot_duration_minutes", header: t("admin.slotDurationMinutes") },
    {
      accessorKey: "is_active",
      header: t("admin.active"),
      cell: ({ row }) => (
        <Badge variant={(row.original.is_active ? "success" : "secondary") as BadgeProps["variant"]}>
          {row.original.is_active ? t("common.yes") : t("common.no")}
        </Badge>
      ),
    },
    {
      id: "schedules",
      header: t("admin.schedule"),
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" onClick={() => setSchedulesFor(row.original)}>
          <CalendarClock className="h-4 w-4" /> {t("admin.schedule")}
        </Button>
      ),
    },
  ];

  const siteOptions = (sites.data?.items ?? []).map((s) => ({
    value: s.id.toString(),
    label: `${s.site_code} — ${s.name}`,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{t("nav.bays")}</h1>
        </div>
        <div className="w-64">
          <Label>{t("admin.site")}</Label>
          <SelectField
            label=""
            value={siteFilter}
            onChange={setSiteFilter}
            options={[{ value: "", label: t("admin.none") }, ...siteOptions]}
          />
        </div>
      </div>

      <FullCrudPage<Bay, BayForm>
        title=""
        entityType="bay"
        getRecord={getBay}
        columns={columns}
        data={bays.data ?? []}
        total={bays.data?.length ?? 0}
        isLoading={bays.isLoading}
        limit={bays.data?.length ?? 0}
        offset={0}
        onPageChange={() => undefined}
        queryKey="bays"
        emptyForm={emptyForm}
        toForm={(r) => ({
          site_id: r.site_id.toString(),
          name: r.name,
          slot_duration_minutes: r.slot_duration_minutes.toString(),
          is_active: r.is_active,
          loads_from_rear: r.loads_from_rear,
          loads_from_left: r.loads_from_left,
          loads_from_right: r.loads_from_right,
          max_vehicle_length_mm:
            r.max_vehicle_length_mm === null ? "" : String(r.max_vehicle_length_mm),
        })}
        canSubmit={(f) => Boolean(f.site_id && f.name)}
        renderForm={(form, setForm) => (
          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label={t("admin.site")}
              value={form.site_id}
              onChange={(v) => setForm({ ...form, site_id: v })}
              options={siteOptions}
            />
            <TextField
              label={t("admin.bay")}
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
            <NumberField
              label={t("admin.slotDurationMinutes")}
              value={form.slot_duration_minutes}
              onChange={(v) => setForm({ ...form, slot_duration_minutes: v })}
            />
            <CheckboxField
              label={t("admin.active")}
              checked={form.is_active}
              onChange={(v) => setForm({ ...form, is_active: v })}
            />
            <CheckboxField
              label={t("admin.sideRear")}
              checked={form.loads_from_rear}
              onChange={(v) => setForm({ ...form, loads_from_rear: v })}
            />
            <CheckboxField
              label={t("admin.sideLeft")}
              checked={form.loads_from_left}
              onChange={(v) => setForm({ ...form, loads_from_left: v })}
            />
            <CheckboxField
              label={t("admin.sideRight")}
              checked={form.loads_from_right}
              onChange={(v) => setForm({ ...form, loads_from_right: v })}
            />
            <NumberField
              label={t("admin.maxVehicleLengthMm")}
              value={form.max_vehicle_length_mm}
              onChange={(v) => setForm({ ...form, max_vehicle_length_mm: v })}
            />
          </div>
        )}
        onSave={async (mode, form, record) => {
          const payload = {
            site_id: Number(form.site_id),
            name: form.name,
            slot_duration_minutes: form.slot_duration_minutes
              ? Number(form.slot_duration_minutes)
              : 60,
            is_active: form.is_active,
            loads_from_rear: form.loads_from_rear,
            loads_from_left: form.loads_from_left,
            loads_from_right: form.loads_from_right,
            max_vehicle_length_mm: form.max_vehicle_length_mm
              ? Number(form.max_vehicle_length_mm)
              : null,
          };
          return mode === "edit" && record ? updateBay(record.id, payload) : createBay(payload);
        }}
        onDelete={(record) => deleteBay(record.id)}
        recordTitle={(r) => r.name}
        detailFields={(r) => [
          { label: t("admin.site"), value: siteName(r.site_id), field: "site_id" },
          { label: t("admin.bay"), value: r.name, field: "name" },
          {
            label: t("admin.slotDurationMinutes"),
            value: r.slot_duration_minutes,
            field: "slot_duration_minutes",
          },
          {
            label: t("admin.active"),
            value: r.is_active ? t("common.yes") : t("common.no"),
            field: "is_active",
          },
          {
            label: t("admin.loadingSides"),
            value: [
              r.loads_from_rear ? t("admin.sideRear") : null,
              r.loads_from_left ? t("admin.sideLeft") : null,
              r.loads_from_right ? t("admin.sideRight") : null,
            ]
              .filter(Boolean)
              .join(" + ") || "—",
          },
          {
            label: t("admin.maxVehicleLengthMm"),
            value: r.max_vehicle_length_mm ?? "—",
            field: "max_vehicle_length_mm",
          },
        ]}
      />

      <Dialog open={schedulesFor !== null} onOpenChange={(o) => !o && setSchedulesFor(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {t("admin.schedule")} — {schedulesFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <ScheduleForm
              key={schedulesFor?.id ?? "none"}
              onAdd={(data) => addSchedule.mutate(data)}
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.weekday")}</TableHead>
                  <TableHead>{t("admin.opensAt")}</TableHead>
                  <TableHead>{t("admin.closesAt")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(schedules.data ?? []).map((window) => (
                  <TableRow key={window.id}>
                    <TableCell>{WEEKDAYS[window.weekday]}</TableCell>
                    <TableCell>{window.opens_at}</TableCell>
                    <TableCell>{window.closes_at}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSchedule.mutate(window.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScheduleForm({ onAdd }: { onAdd: (d: { weekday: number; opens_at: string; closes_at: string }) => void }) {
  const { t } = useTranslation();
  const [weekday, setWeekday] = useState("1");
  const [opens, setOpens] = useState("07:00");
  const [closes, setCloses] = useState("21:00");

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
      <div className="space-y-2">
        <Label>{t("admin.weekday")}</Label>
        <SelectField
          label=""
          value={weekday}
          onChange={setWeekday}
          options={WEEKDAYS.map((day, index) => ({ value: String(index), label: day }))}
        />
      </div>
      <div className="space-y-2">
        <Label>{t("admin.opensAt")}</Label>
        <Input type="time" value={opens} onChange={(e) => setOpens(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label>{t("admin.closesAt")}</Label>
        <Input type="time" value={closes} onChange={(e) => setCloses(e.target.value)} />
      </div>
      <Button
        onClick={() =>
          onAdd({ weekday: Number(weekday), opens_at: opens, closes_at: closes })
        }
      >
        <Plus className="h-4 w-4" /> {t("admin.addSchedule")}
      </Button>
    </div>
  );
}
